import React from 'react';
import {
    Grid2,
    IconButton,
    Table,
    TableContainer,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import SkeletonTable from '../../../components/atom/SkeletonTable';
import { EMAIL_LOG_TYPES, TABLE_ROW_COUNT_OPTIONS } from '../../../helpers/constants';
import SortableTableCell from '../../../components/atom/SortableTableCell';
import { MODAL_TYPE, useModals } from '../../../hooks/useModal';
import { handleChangeRowsPerPage, handlePageChange } from '../../../helpers/handleTableFunctions';
import { formatDateTimeInVenueTz } from '../../../helpers/formatDateTime';
import { useTranslation } from 'react-i18next';

const EmailLogsTable = ({
    rows,
    limit,
    page,
    setParams,
    total,
    loading,
    sort,
    sortDir,
    setSorting,
    reloadData,
}) => {
    const { t } = useTranslation();
    const { showModal } = useModals();

    return (
        <Grid2 size={{ xs: 12 }}>
            <TableContainer
                sx={{
                    // Cap the height so the table scrolls inside its own region and the
                    // sticky header pins to the top (mirrors the audit-logs table).
                    maxHeight: { xs: '60dvh', md: 'calc(100dvh - 256px)' },
                }}
            >
                <Table stickyHeader sx={{ minWidth: 900 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('emaillogs.columns.userName')}</TableCell>
                            <SortableTableCell
                                arrow={sort === 'to' ? sortDir : ''}
                                onClick={() => setSorting('to')}
                            >
                                {t('emaillogs.columns.to')}
                            </SortableTableCell>
                            <SortableTableCell
                                arrow={sort === 'subject' ? sortDir : ''}
                                onClick={() => setSorting('subject')}
                            >
                                {t('emaillogs.columns.subject')}
                            </SortableTableCell>
                            <SortableTableCell
                                arrow={sort === 'result' ? sortDir : ''}
                                onClick={() => setSorting('result')}
                            >
                                {t('emaillogs.columns.result')}
                            </SortableTableCell>
                            <SortableTableCell
                                arrow={sort === 'type' ? sortDir : ''}
                                onClick={() => setSorting('type')}
                            >
                                {t('emaillogs.columns.type')}
                            </SortableTableCell>
                            <TableCell align="center">{t('emaillogs.columns.content')}</TableCell>
                            <SortableTableCell
                                arrow={sort === 'created_at' ? sortDir : ''}
                                onClick={() => setSorting('created_at')}
                            >
                                {t('emaillogs.columns.created')}
                            </SortableTableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <SkeletonTable rows={limit} cells={7} />
                        ) : (
                            rows?.map((row) => (
                                <React.Fragment key={row.id}>
                                    <TableRow>
                                        <TableCell>{row.user_name || '-'}</TableCell>
                                        <TableCell>{row.to}</TableCell>
                                        <TableCell>{row.subject}</TableCell>
                                        <TableCell>{row.result || '-'}</TableCell>
                                        <TableCell>{row.type}</TableCell>
                                        <TableCell align="center">
                                            {row.type === EMAIL_LOG_TYPES.EMAIL && (
                                                <Tooltip title={t('emaillogs.viewEmail')}>
                                                    <IconButton
                                                        onClick={() => {
                                                            showModal(MODAL_TYPE.EMAIL_LOGS, {
                                                                title: t('emaillogs.mail'),
                                                                content: row,
                                                                reloadData: () => reloadData(),
                                                            });
                                                        }}
                                                    >
                                                        <RemoveRedEyeIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {formatDateTimeInVenueTz(row.created_at)}
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                count={total || 0}
                rowsPerPage={limit}
                component="div"
                page={page}
                onPageChange={(_, newPage) => handlePageChange(newPage, setParams)}
                rowsPerPageOptions={TABLE_ROW_COUNT_OPTIONS}
                onRowsPerPageChange={(event) => handleChangeRowsPerPage(event, setParams)}
            />
        </Grid2>
    );
};

export default EmailLogsTable;
